import React, { useEffect, useState, useContext, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { UserContext } from '../App';

// ICT Loss icon - red downward arrow with circle
const ICTLossIcon = () => (
  <svg className="w-5 h-5 text-[#EF4444] dark:text-[#DC2626]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    {/* Circle background */}
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" fill="none" />
    {/* Downward arrow */}
    <path d="M12 8v8M8 12l4-4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ICTEmailToast = ({ show, onClose }) => {
  const { currentUser, setICTEmailFound } = useContext(UserContext);
  const [isExpanded, setIsExpanded] = useState(false);
  const hasSavedAchievement = useRef(false);

  // Track achievement when email is first shown
  useEffect(() => {
    if (show && !hasSavedAchievement.current && currentUser && setICTEmailFound) {
      hasSavedAchievement.current = true;
      setICTEmailFound(currentUser.uid);
    }
  }, [show, currentUser, setICTEmailFound]);

  useEffect(() => {
    if (show) {
      let timer;
      
      if (isExpanded) {
        // When expanded, stay open for 20 seconds
        timer = setTimeout(() => {
          onClose();
        }, 20000);
      } else {
        // When collapsed, auto-dismiss after 10 seconds
        timer = setTimeout(() => {
          onClose();
        }, 10000);
      }

      return () => clearTimeout(timer);
    } else {
      // Reset expanded state when toast is closed
      setIsExpanded(false);
    }
  }, [show, onClose, isExpanded]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, x: 400, y: -20 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          exit={{ opacity: 0, x: 400 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className={`fixed top-4 right-4 z-[9999] bg-white dark:bg-[#1F2937] border border-[#E5E7EB] dark:border-[#374151] rounded-lg shadow-elevated overflow-hidden cursor-pointer ${isExpanded ? 'w-80' : 'w-auto min-w-[180px]'}`}
          onClick={() => !isExpanded && setIsExpanded(true)}
        >
          {!isExpanded ? (
            // Collapsed view - just icon and "From: ICT"
            <div className="flex items-center justify-between p-3 gap-3">
              <div className="flex items-center gap-3">
                <ICTLossIcon />
                <span className="text-sm font-medium text-[#111827] dark:text-[#F9FAFB] whitespace-nowrap">From: ICT</span>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                className="text-[#6B7280] dark:text-[#9CA3AF] hover:text-[#111827] dark:hover:text-[#F9FAFB] transition-colors flex-shrink-0"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          ) : (
            // Expanded view - full message
            <>
              {/* Header */}
              <div className="flex items-center justify-between p-3 bg-[#F3F4F6] dark:bg-[#374151] border-b border-[#E5E7EB] dark:border-[#374151]">
                <div className="flex items-center gap-2">
                  <ICTLossIcon />
                  <span className="text-sm font-semibold text-[#111827] dark:text-[#F9FAFB]">New Message</span>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                  }}
                  className="text-[#6B7280] dark:text-[#9CA3AF] hover:text-[#111827] dark:hover:text-[#F9FAFB] transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-4">
                <div className="mb-2">
                  <div className="text-xs text-[#6B7280] dark:text-[#9CA3AF] mb-1">From:</div>
                  <div className="text-sm font-medium text-[#111827] dark:text-[#F9FAFB]">ICT</div>
                </div>
                <div className="mb-3">
                  <div className="text-xs text-[#6B7280] dark:text-[#9CA3AF] mb-1">Subject:</div>
                  <div className="text-sm font-semibold text-[#111827] dark:text-[#F9FAFB]">It's not supposed to be easy</div>
                </div>
                <div className="text-sm text-[#374151] dark:text-[#D1D5DB] leading-relaxed">
                  "If you can't handle a loss, you can't handle a win. The market is a mirror of your own discipline."
                </div>
              </div>
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ICTEmailToast;
